import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import authAPI from "../../../services/authApi";
import clinicAPI from "../../../services/clinicApi";
import { hasAccessToken } from "../../../utils/authStorage";
import { getClinicPortalLinks } from "../../../utils/clinicPortalLinks";
import { DOCTORS_BASE } from "./doctorConstants";
import s from "../clinic.module.css";
import {
  Button,
  Card,
  Field,
  Input,
  Select,
  Textarea,
  FormSection,
  FormGrid,
  useToast,
} from "../../ui";

export default function AdminDoctorNew() {
  const navigate = useNavigate();
  const toast = useToast();
  const { tenant } = useSelector((state) => state.auth);
  const [branches, setBranches] = useState([]);
  const [specializations, setSpecializations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    fullName: "",
    specialization_id: "",
    description: "",
    email: "",
    password: "",
    branchIds: [],
  });

  const staffPortalUrl =
    getClinicPortalLinks(tenant?.subdomain).find((l) => l.id === "staff")?.url ||
    "";

  useEffect(() => {
    clinicAPI
      .getSpecializations()
      .then((r) =>
        setSpecializations(
          (r.specializations || []).filter((x) => x.is_active !== false),
        ),
      )
      .catch(() => setSpecializations([]));
    clinicAPI.getBranches().then((r) => {
      const list = (r.branches || []).filter((b) => b.is_active !== false);
      setBranches(list);
    });
  }, []);

  const toggleBranch = (id) => {
    setForm((f) => ({
      ...f,
      branchIds: f.branchIds.includes(id)
        ? f.branchIds.filter((x) => x !== id)
        : [...f.branchIds, id],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!hasAccessToken()) {
      toast.error("Сессия истекла. Войдите снова.");
      return;
    }
    if (!form.password) {
      toast.error("Укажите пароль");
      return;
    }
    setLoading(true);
    try {
      const res = await authAPI.inviteTrainer({
        email: form.email.trim(),
        password: form.password,
        fullName: form.fullName.trim(),
        specialization_id: form.specialization_id || undefined,
        description: form.description?.trim() || undefined,
        branchIds: form.branchIds.length ? form.branchIds : undefined,
      });
      if (res?.result !== 0) {
        toast.error(res?.error || "Не удалось добавить сотрудника");
        return;
      }
      toast.success(
        `Тренер «${res.trainer?.full_name || res.doctor?.full_name}» добавлен${staffPortalUrl ? ` · ${staffPortalUrl}` : ""}`,
        7000,
      );
      const tid = res.trainer?.id || res.doctor?.id;
      if (tid) {
        navigate(`${DOCTORS_BASE}/${tid}`);
      } else {
        navigate(DOCTORS_BASE);
      }
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={s.patientPage}>
      <nav className={`${s.patientBreadcrumbs} ${s.patientPageBlock}`}>
        <Link to="/manage/app/team">Команда</Link>
        <span aria-hidden>/</span>
        <span>Новый сотрудник</span>
      </nav>

      <Card
        title="Добавить сотрудника"
        subtitle="Отдельный вход для тренера — журнал и расписание только по его занятиям"
      >
        <form onSubmit={submit}>
          <FormSection title="Данные тренера">
            <FormGrid>
              <Field label="ФИО *">
                <Input
                  required
                  value={form.fullName}
                  onChange={(e) =>
                    setForm({ ...form, fullName: e.target.value })
                  }
                />
              </Field>
              <Field label="Специализация">
                <Select
                  value={form.specialization_id}
                  onChange={(e) =>
                    setForm({ ...form, specialization_id: e.target.value })
                  }
                >
                  <option value="">Не указана</option>
                  {specializations.map((sp) => (
                    <option key={sp.id} value={sp.id}>
                      {sp.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Описание">
                <Textarea
                  rows={2}
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                />
              </Field>
            </FormGrid>
          </FormSection>

          <FormSection title="Доступ в портал">
            <FormGrid>
              <Field label="Email *">
                <Input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) =>
                    setForm({ ...form, email: e.target.value })
                  }
                />
              </Field>
              <Field label="Пароль *" hint="Минимум 8 символов">
                <Input
                  type="password"
                  required
                  minLength={8}
                  value={form.password}
                  onChange={(e) =>
                    setForm({ ...form, password: e.target.value })
                  }
                />
              </Field>
            </FormGrid>
          </FormSection>

          <FormSection title="Залы">
            <div className={s.branchCheckGrid}>
              {branches.map((b) => (
                <label key={b.id} className={s.branchCheckItem}>
                  <input
                    type="checkbox"
                    checked={form.branchIds.includes(b.id)}
                    onChange={() => toggleBranch(b.id)}
                  />
                  <span>{b.name}</span>
                </label>
              ))}
            </div>
          </FormSection>

          <div style={{ display: "flex", gap: 8 }}>
            <Button type="submit" disabled={loading}>
              {loading ? "Создание…" : "Пригласить тренера"}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate(DOCTORS_BASE)}
            >
              Отмена
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
